import Button from 'components/Button';
import Icon from 'components/Icon';
import TaxCombinationModal from 'components/ModalTaxCombination';
import SortableContainer from 'components/Sortable/SortableContainer';
import SortableHandle from 'components/Sortable/SortableHandle';
import SortableItem from 'components/Sortable/SortableItem';
import TooltipWrapper from 'components/Tooltip';
import {uniqueId} from 'lodash';
import {FC, useCallback, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {twMerge} from 'tailwind-merge';
import {t} from 'ttag';
import {TaxCombination} from 'utils/tax';
import {taxCombinationLabel} from 'utils/tax-label';

import {FormFields} from '../../services';

const Section: FC = () => {
    const [isModalOpen, setModalOpen] = useState<boolean>(false);

    const {setValue, watch} = useFormContext<FormFields>();
    const items = watch('taxCombinations');

    const numberItems = items.length;
    const canCreate = numberItems < 10;
    const canDelete = numberItems > 1;

    const onItemCreate = useCallback(() => {
        setModalOpen(true);
    }, []);

    const onItemRemove = useCallback(
        (index: number) => {
            const newItems = [...items];
            newItems.splice(index, 1);
            setValue('taxCombinations', newItems);
        },
        [items, setValue],
    );

    const onModalCancel = useCallback(() => {
        setModalOpen(false);
    }, []);

    const onModalConfirm = useCallback(
        (taxCombination: TaxCombination) => {
            const newTaxCombinations = [...items];
            newTaxCombinations.push({
                ...taxCombination,
                id: `new_${uniqueId()}`,
            });

            setValue('taxCombinations', newTaxCombinations);
            setModalOpen(false);
        },
        [items, setValue],
    );

    const onModalCheckDuplicate = useCallback(
        ({isTaxInclusive, taxRate, taxType}: TaxCombination) =>
            items.some(
                (item) =>
                    item.taxType === taxType &&
                    item.taxRate === taxRate &&
                    item.isTaxInclusive === isTaxInclusive,
            ),
        [items],
    );

    return (
        <div className="flex max-w-xl flex-col gap-y-3" role="rowgroup">
            <div className="rounded border border-grey-400">
                <SortableContainer
                    items={items}
                    onSortChange={(newItems) => {
                        setValue('taxCombinations', [...newItems]);
                    }}
                    renderItem={(item, index) => (
                        <SortableItem
                            className={twMerge(
                                'flex items-center p-0.5 px-2 border-b border-grey-400',
                                index === 0 && 'rounded-t',
                                index % 2 === 1 && 'bg-grey-100',
                                index === items.length - 1 &&
                                    'border-b-0 rounded-b',
                            )}
                            id={item.id}
                            key={item.id}
                        >
                            <SortableHandle
                                className="w-10 p-1 pl-1.5"
                                id={item.id}
                            >
                                <Icon
                                    className="py-1"
                                    icon="slider_handle"
                                    size="base"
                                />
                            </SortableHandle>
                            <div className="flex-1" role="presentation">
                                {taxCombinationLabel(item)}
                            </div>
                            <TooltipWrapper
                                message={
                                    canDelete
                                        ? ''
                                        : t`Minimum one (1) Tax Setting`
                                }
                            >
                                <Button
                                    disabled={!canDelete}
                                    icon="close_small"
                                    id={`delete-${item.id}`}
                                    kind="secondary"
                                    onClick={() => onItemRemove(index)}
                                    size="xs"
                                />
                            </TooltipWrapper>
                        </SortableItem>
                    )}
                />
            </div>
            <div className="flex gap-x-2">
                <TooltipWrapper
                    message={
                        canCreate ? '' : t`Maximum 10 Favorite Tax Settings`
                    }
                >
                    <Button
                        disabled={!canCreate}
                        icon="add"
                        id="add"
                        kind="secondary"
                        onClick={onItemCreate}
                        size="sm"
                    >
                        {t`Add Tax Setting`}
                    </Button>
                </TooltipWrapper>
                <TaxCombinationModal
                    checkDuplicate={onModalCheckDuplicate}
                    isOpen={isModalOpen}
                    onCancel={onModalCancel}
                    onConfirm={onModalConfirm}
                    showHelperText={true}
                />
            </div>
        </div>
    );
};

export default Section;
